/**
 * « M'asseoir à côté » : ouvrir la séance à deux d'un exercice (lot P4).
 *
 * Le parent tient le rôle du tuteur, cahier ouvert à côté de l'enfant : l'écran
 * lui donne l'énoncé, les indices un échelon à la fois, et le corrigé une fois
 * qu'une réponse a été donnée (`core-api` §3.30, `web-pwa` §17.9). Le bouton
 * **ouvre ou rouvre** : le serveur rend la séance déjà commencée pour la même
 * ligne, si bien qu'un parent qui revient ne remet pas le compteur d'indices à
 * zéro.
 *
 * **Vit dans `ui/`** parce que la V1 (`parent/ProchainScreen`, un exercice du
 * soir) et la V2 (feuilles, carnet) s'en servent et qu'aucune des deux
 * n'importe l'autre — la même raison que `ui/impression`. **La coquille enfant
 * ne s'en sert pas** : les routes sont celles du parent, et un test balaie
 * `src/child/**`.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { LigneDEntrainementOut } from "@/api/schema";

/** Ce qu'on ouvre : une ligne de feuille (ou de fiche du carnet), ou un exercice du soir. */
export type ASAsseoir = { ligneId: string } | { exerciseId: string };

/** L'adresse de l'écran d'une séance, sous les entraînements de l'enfant. */
export function seanceDe(childId: string, seanceId: string): string {
  return `/parents/enfant/${childId}/entrainements/seance/${seanceId}`;
}

/**
 * Le bouton a-t-il un sens sur cette ligne ? Pure, exportée pour être testée.
 *
 * Pas sur une feuille ouverte — l'enfant ne l'a pas reçue ; pas sur une dictée,
 * qui se lit à voix haute depuis la feuille et n'a pas d'indices ; pas sur une
 * ligne déjà jugée. Le serveur refuse les trois de toute façon : cacher le
 * bouton évite seulement un clic qui finit en message d'erreur.
 */
export function peutSAsseoir(ligne: LigneDEntrainementOut, feuilleClose: boolean): boolean {
  if (!feuilleClose) return false;
  if (ligne.verdict) return false;
  return ligne.forme_papier !== "dictee";
}

export function AsseoirACote({ childId, cible }: { childId: string; cible: ASAsseoir }) {
  const navigate = useNavigate();
  const [envoi, setEnvoi] = useState(false);
  const [panne, setPanne] = useState<string | null>(null);

  const ouvrir = async () => {
    setEnvoi(true);
    setPanne(null);
    try {
      const seance = await call("ouvrirUneSeanceADeux", {
        params: { child_id: childId },
        body:
          "ligneId" in cible
            ? { ligne_id: cible.ligneId, exercise_id: null }
            : { ligne_id: null, exercise_id: cible.exerciseId },
      });
      navigate(seanceDe(childId, seance.id));
    } catch (echec) {
      setPanne(echec instanceof ApiError ? echec.message : "La séance n'a pas pu s'ouvrir.");
      setEnvoi(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn--quiet"
        disabled={envoi}
        onClick={() => void ouvrir()}
      >
        M'asseoir à côté
      </button>
      {panne ? <span className="muted small">{panne}</span> : null}
    </>
  );
}
